import { ReactElement, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { conditionallyPluralize } from '../../utils/text-utils';
import { fetchUsersByDomain } from './client-data-mapping-api';

// Max users the tooltip renders. Backend returns up to LIMIT + 1 rows; if the (LIMIT + 1)th
// row is present, the tooltip renders the first (LIMIT - 1) users and a "+N more" summary
// line -- so a user never sees exactly LIMIT rows immediately followed by a "+1 more" hint
// (which would just be noise). Kept file-private so client-data-mapping-constants can hold a
// tab-config renderer that references this component without creating an import cycle.
const USERS_BY_DOMAIN_LIMIT = 10;

interface DomainUsersTooltipProps {
  domain: string;
  children: ReactElement;
}

// Hovering the wrapped child renders a popover listing BPM users at `domain`. The list is
// lazy-fetched: the query is only enabled after the first hover, so we don't fan out a per-row
// request when the table paints. Results are cached by React Query on the domain string.
//
// The endpoint returns up to USERS_BY_DOMAIN_LIMIT + 1 rows -- if we got exactly LIMIT + 1
// users back, we show (LIMIT - 1) rows plus a "+N more" hint so the reader never sees a full
// list followed by "+1 more" (which would just be noise). If we got LIMIT or fewer rows, we
// show them all -- there is no additional overflow to summarize.
export const DomainUsersTooltip = ({ domain, children }: DomainUsersTooltipProps) => {
  const [hoveredOnce, setHoveredOnce] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['usersByDomain', domain],
    queryFn: () => fetchUsersByDomain(domain),
    enabled: hoveredOnce,
    staleTime: 5 * 60 * 1000
  });

  return (
    <OverlayTrigger
      placement='top'
      delay={{ show: 300, hide: 100 }}
      onToggle={next => next && setHoveredOnce(true)}
      overlay={
        <Popover id={`domain-users-${domain}`} className='domain-users-tooltip-popover'>
          <Popover.Body>
            <DomainUsersBody isLoading={isLoading} error={error} data={data} />
          </Popover.Body>
        </Popover>
      }
    >
      {children}
    </OverlayTrigger>
  );
};

interface DomainUsersBodyProps {
  isLoading: boolean;
  error: Error | null;
  data: { users: { email: string }[] } | undefined;
}

const DomainUsersBody = ({ isLoading, error, data }: DomainUsersBodyProps) => {
  if (isLoading) return <span>Loading users…</span>;
  if (error) return <span>Couldn&apos;t load users: {error.message}</span>;
  if (!data || data.users.length === 0) return <span>No BPM users at this domain.</span>;

  // Server caps at LIMIT + 1. If we got exactly LIMIT + 1, we know at least one more exists
  // beyond what we render -- collapse the last two visible rows into "+N more" so the
  // "there's more here than fits" signal is obvious without pretending we know the exact
  // total count.
  const hasOverflow = data.users.length > USERS_BY_DOMAIN_LIMIT;
  const visible = hasOverflow ? data.users.slice(0, USERS_BY_DOMAIN_LIMIT - 1) : data.users;
  const remaining = data.users.length - visible.length;

  return (
    <ul className='domain-users-tooltip-list'>
      {visible.map(u => (
        <li key={u.email}>{u.email}</li>
      ))}
      {hasOverflow && (
        <li className='domain-users-tooltip-more'>
          +{remaining} more {conditionallyPluralize(remaining, 'user')}
        </li>
      )}
    </ul>
  );
};
